import React from "react";
import Badge from '@mui/material/Badge';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControl from '@mui/material/FormControl';
import PendingIcon from '@mui/icons-material/Pending';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useTheme } from "@mui/styles";
import { useSelector } from "react-redux";
import FormControlLabel from '@mui/material/FormControlLabel';
import FilterTierDropdown from "../../../../../components/DropDowns/Filters/FilterTierDropdown";
import SortCategoryDropdown from "../../../../../components/DropDowns/Sorts/SortCategoryDropdown";
import FilterLeadTypeDropdown from "../../../../../components/DropDowns/Filters/FilterLeadTypeDropdown";
import InvestigationTagsMultiSelect from "../../../../../components/MultiSelect/InvestigationTagsMultiSelect";
import { styled } from "@mui/material/styles";
import { selectTeamLeadsAwaitingUpdate, selectTeamLeadsAwaitingVerification } from "../../../../../slices/teamActiveLeadsSlice";
import { selectAwaitingUpdatesCount, selectClosingCount, selectInvestigatingCount, selectNewAwaitingActionCount, selectNewAwaitingActionPresent, selectNewAwaitingUpdatesPresent, selectNewClosingPresent, selectNewInvestigatingPresent } from "../../../../../slices/userNotificationsSlice";

const Controls = ({ leadStatusFilter, leadTypeFilter, leadTierFilter, handleLeadStatusFilterChange, leadCategorySort, handleLeadSortCategoryChange, leadSortMethod, handleLeadSortMethodChange, handleLeadTierFilterChange, handleLeadTypeFilterChange, leadNewFilter, handleLeadNewFilterChange, leadTagFilter, handleLeadTagFilterChange, teamLeadTags, memberAwaitingActionAmount, memberInvestigatingAmount, memberClosingAmount, memberAwaitingUpdateAmount, teamAwaitingActionAmount, teamInvestigatingAmount, teamClosingAmount, teamAwaitingUpdateAmount, dashPerspective }) => {
  const theme = useTheme()

  const badgeStyle = {
    "& .MuiBadge-badge": {
      color: `${theme.palette.common.white}`,
      backgroundColor: `${theme.palette.common.error}`,
      fontWeight: '800',
      fontSize: "1rem",
      height: "1.4rem",
      padding: "0 0.5rem",
      borderRadius: "1rem",
      minWidth: "1.2rem",
      margin: '0.2rem 0 0 0.5em'
    }
  }

  const newAwaitingActionCount = useSelector(selectNewAwaitingActionCount)
  const awaitingUpdatesCount = useSelector(selectAwaitingUpdatesCount)
  const investigatingCount = useSelector(selectInvestigatingCount)
  const closingCount = useSelector(selectClosingCount)

  const teamLeadsAwaitingUpdate = useSelector(selectTeamLeadsAwaitingUpdate)
  const teamLeadsAwaitingVerification = useSelector(selectTeamLeadsAwaitingVerification)
  const newAwaitingActionPresent = useSelector(selectNewAwaitingActionPresent)
  const newInvestigatingPresent = useSelector(selectNewInvestigatingPresent)
  const newClosingPresent = useSelector(selectNewClosingPresent)
  const newAwaitingUpdatesPresent = useSelector(selectNewAwaitingUpdatesPresent)

  const filterStatusControlProps = (item, memberAmount, teamAmount) => ({
    checked: leadStatusFilter === item,
    onChange: handleLeadStatusFilterChange,
    value: item,
    label: dashPerspective === 'team' ? `(${teamAmount}) ${item}` : `(${memberAmount}) ${item}`,
    labelPlacement: "end",
    className: item === "Awaiting Action" ? "awaitingAction" : "",
    sx: {
      color: `${theme.palette.common.black}`,
    }
  });
  const filterNewControlProps = (item) => ({
    checked: leadNewFilter === item,
    onChange: handleLeadNewFilterChange,
    value: item,
    label: item,
    labelPlacement: "start",
    sx: {
      color: `${theme.palette.common.black}`,
      margin: '0 1rem 0 0'
    }
  });
  const sortMethodControlProps = (item) => ({
    checked: leadSortMethod === item,
    onChange: handleLeadSortMethodChange,
    value: item,
    label: item,
    labelPlacement: "end",
    sx: {
      color: `${theme.palette.common.black}`,
    }
  });
  const colorProps = () => ({
    sx: {
      padding: "0.5rem 0.5rem 0.5rem 0",
      color: `${theme.palette.common.grayBorder}`,
      '&.Mui-checked': {
        color: `${theme.palette.primary.main}`,
      },
      '.awaitingAction &.Mui-checked': {
        color: '#c2410c',
      },
    }
  });
  const colorPropsRow = () => ({
    sx: {
      padding: "0.5rem 0 0.5rem 0.5rem",
      color: `${theme.palette.common.grayBorder}`,
      '&.Mui-checked': {
        color: `${theme.palette.primary.main}`,
      },
    }
  })

  const showNewOnlyFilter =
    (leadStatusFilter === "Awaiting Action" && newAwaitingActionPresent) ||
    (leadStatusFilter === "Investigating" && newInvestigatingPresent) ||
    (leadStatusFilter === "Closing" && newClosingPresent) ||
    (leadStatusFilter === "Awaiting PR Updates" && newAwaitingUpdatesPresent);

  const renderStatusOption = (statusLabel, memberAmount, teamAmount, badgeCount) => {
    const option = (
      <FormControlLabel
        control={<Radio {...colorProps()} />}
        {...filterStatusControlProps(statusLabel, memberAmount, teamAmount)}
      />
    );

    if (badgeCount !== undefined && badgeCount > 0) {
      return (
        <Badge
          key={statusLabel}
          badgeContent={badgeCount}
          color="error"
          sx={badgeStyle}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          {option}
        </Badge>
      );
    }

    return <React.Fragment key={statusLabel}>{option}</React.Fragment>;
  };

  return (
    <ControlsShell>
      <PanelSection>
        <SectionHeader>
          <HeaderEyebrow>Queue Controls</HeaderEyebrow>
          <HeaderTitle>Filter</HeaderTitle>
        </SectionHeader>

        <FormControl fullWidth>
          <SectionLabel>By Status</SectionLabel>

          <StyledRadioGroup>
            {renderStatusOption(
              "Awaiting Action",
              memberAwaitingActionAmount,
              teamAwaitingActionAmount,
              newAwaitingActionCount
            )}

            {renderStatusOption(
              "Investigating",
              memberInvestigatingAmount,
              teamInvestigatingAmount,
              investigatingCount
            )}

            {renderStatusOption(
              "Closing",
              memberClosingAmount,
              teamClosingAmount,
              closingCount
            )}

            <PanelDivider />

            {renderStatusOption(
              "Awaiting PR Updates",
              memberAwaitingUpdateAmount,
              teamAwaitingUpdateAmount,
              awaitingUpdatesCount
            )}
          </StyledRadioGroup>
        </FormControl>

        <QueueStatsPanel>
          <QueueStat>
            <strong>{teamLeadsAwaitingUpdate}</strong>
            <PendingIcon />
            <span>pending</span>
          </QueueStat>

          <QueueStat>
            <strong>{teamLeadsAwaitingVerification}</strong>
            <TaskAltIcon />
            <span>ready to verify</span>
          </QueueStat>
        </QueueStatsPanel>
      </PanelSection>

      <PanelSection>
        <SectionLabel>By Tier</SectionLabel>
        <FilterTierDropdown
          value={leadTierFilter}
          handleChange={handleLeadTierFilterChange}
        />

        <SectionLabel className="spaced">By Investigation Tag</SectionLabel>
        <InvestigationTagsMultiSelect
          value={leadTagFilter}
          handleChange={handleLeadTagFilterChange}
          items={teamLeadTags}
        />

        <SectionLabel className="spaced">By Type</SectionLabel>
        <FilterLeadTypeDropdown
          value={leadTypeFilter}
          handleChange={handleLeadTypeFilterChange}
        />
      </PanelSection>

      {showNewOnlyFilter && (
        <PanelSection className="highlight">
          <NewOnlyHeader>
            <span>New Only</span>
          </NewOnlyHeader>

          <RadioGroup row>
            <FormControlLabel
              control={<Radio {...colorPropsRow()} />}
              {...filterNewControlProps("true")}
            />
            <FormControlLabel
              control={<Radio {...colorPropsRow()} />}
              {...filterNewControlProps("false")}
            />
          </RadioGroup>
        </PanelSection>
      )}

      <PanelSection>
        <SectionHeader className="sort">
          <HeaderEyebrow>Ordering</HeaderEyebrow>
          <HeaderTitle>Sort</HeaderTitle>
        </SectionHeader>

        <SectionLabel>Category</SectionLabel>
        <SortCategoryDropdown
          value={leadCategorySort}
          handleChange={handleLeadSortCategoryChange}
        />

        <SectionLabel className="spaced">Method</SectionLabel>
        <FormControl fullWidth>
          <StyledRadioGroup>
            <FormControlLabel
              control={<Radio {...colorProps()} />}
              {...sortMethodControlProps("High-Low")}
            />
            <FormControlLabel
              control={<Radio {...colorProps()} />}
              {...sortMethodControlProps("Low-High")}
            />
          </StyledRadioGroup>
        </FormControl>
      </PanelSection>
    </ControlsShell>
  );
}

export default Controls;

const ControlsShell = styled("aside")(({ theme }) => ({
  width: "100%",
  height: "fit-content",
  minHeight: "fit-content",
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
  padding: "0.4rem",
  borderRadius: "0.8rem",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.94))",
  boxShadow: "0 0.8rem 1.8rem rgba(15, 23, 42, 0.065)",
}));

const PanelSection = styled("section")(({ theme }) => ({
  borderRadius: "0.8rem",
  border: `0.1rem solid ${theme.palette.common.grayBorder}`,
  background: "rgba(255,255,255,0.82)",
  padding: "0.6rem",
  boxShadow: "0 0.5rem 1.2rem rgba(15, 23, 42, 0.04)",

  "&.highlight": {
    borderColor: "rgba(234,88,12,0.25)",
    background: "rgba(251,146,60,0.08)",
  },
}));

const SectionHeader = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "0.15rem",
  marginBottom: "0.8rem",

  "&.sort": {
    marginBottom: "1rem",
  },
});

const HeaderEyebrow = styled("div")(({ theme }) => ({
  color: theme.palette.common.grayText,
  fontSize: "0.8rem",
  fontWeight: 900,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
}));

const HeaderTitle = styled("div")({
  color: "#0f172a",
  fontSize: "1.55rem",
  fontWeight: 950,
  letterSpacing: 0,
});

const SectionLabel = styled("div")(({ theme }) => ({
  margin: "0 0 0.55rem 0",
  color: "#334155",
  fontSize: "0.95rem",
  fontWeight: 900,
  letterSpacing: "0.08em",
  textTransform: "uppercase",

  "&.spaced": {
    marginTop: "0.8rem",
  },
}));

const StyledRadioGroup = styled(RadioGroup)({
  gap: "0",

  "& .MuiFormControlLabel-root": {
    minHeight: "2rem",
    maxHeight: "2rem",
    margin: 0,
    padding: "0.1rem 0.45rem",
    borderRadius: "0.65rem",
    transition: "background 160ms ease",

    "&:hover": {
      background: "rgba(37,99,235,0.06)",
    },

    "&.awaitingAction:hover": {
      background: "rgba(194,65,12,0.08)",
    },

    "&.awaitingAction:has(.Mui-checked)": {
      background: "rgba(194,65,12,0.1)",
    },
  },

  "& .MuiFormControlLabel-label": {
    color: "#334155",
    fontSize: "0.95rem",
    fontWeight: 800,
  },
});

const PanelDivider = styled("div")(({ theme }) => ({
  width: "100%",
  height: "0.1rem",
  margin: "0.35rem 0",
  background: theme.palette.common.grayBorder,
}));

const QueueStatsPanel = styled("div")(({ theme }) => ({
  marginTop: "0.9rem",
  paddingTop: "0.85rem",
  borderTop: `0.1rem solid ${theme.palette.common.grayBorder}`,
  display: "flex",
  flexDirection: "column",
  gap: "0.45rem",
}));

const QueueStat = styled("div")(({ theme }) => ({
  height: "2.4rem",
  display: "flex",
  alignItems: "center",
  gap: "0.45rem",
  color: "#334155",
  fontSize: "0.95rem",
  fontWeight: 800,

  "& strong": {
    minWidth: "2.4rem",
    height: "1.8rem",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f172a",
    borderRadius: "999px",
    background: "rgba(15,23,42,0.06)",
    fontWeight: 950,
  },

  "& svg": {
    width: "1.5rem",
    height: "1.5rem",
    color: theme.palette.common.grayText,
  },
}));

const NewOnlyHeader = styled("div")({
  marginBottom: "0.5rem",

  "& span": {
    height: "2.4rem",
    display: "inline-flex",
    alignItems: "center",
    padding: "0 0.9rem",
    borderRadius: "999px",
    color: "#9a3412",
    background: "rgba(251,146,60,0.14)",
    border: "0.1rem solid rgba(234,88,12,0.25)",
    fontSize: "0.95rem",
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
});
